import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getInitialServiceState } from './service-slice-definitions';
import SoftwareVersion from '../../models/enums/software-version';
import MissionFilters from '../../models/service/stats/mission-filters';
import { defaultState } from '../general/general-slice-definitions';
import { getAnnouncements, getStatsMissionEntries } from './service-actions';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';

const initialServiceState = getInitialServiceState();

export const softwareVersionStorageName = 'missionsSoftwareVersion';
const SEEN_ANNOUNCEMENT_STORAGE_KEY = 'sphere-seen-announcements';

const serviceSlice = createSlice({
  name: 'service',
  initialState: initialServiceState,
  reducers: {
    resetServiceState(state) {
      // Reset to initial admin state
      return { ...initialServiceState };
    },
    toggleBuildNumberAndVersion(
      state,
      action: PayloadAction<{ buildNumber: string; softwareVersion: SoftwareVersion }>,
    ) {
      const buildIndex = state.missionStats.entries.findIndex(
        (entrySet) =>
          entrySet.buildNumber === action.payload.buildNumber &&
          entrySet.softwareVersion === action.payload.softwareVersion,
      );
      if (buildIndex !== -1) {
        state.missionStats.entries.splice(buildIndex, 1);
      } else {
        state.missionStats.entries.push({
          ...action.payload,
          data: [],
          count: 0,
          rest: defaultState.rest,
          start: null,
          end: null,
        });
      }
    },
    updateMissionFilters(state, action: PayloadAction<Partial<MissionFilters>>) {
      state.missionStats.filters = { ...state.missionStats.filters, ...action.payload };
      if (action.payload.softwareVersion != null) {
        localStorage.setItem(softwareVersionStorageName, action.payload.softwareVersion);
      }
    },
    resetAnnouncementsRestStatus(state) {
      state.announcements.rest = { getStatus: HttpFetchStatus.Idle, getError: null };
    },
    fetchSeenAnnouncementIds(state) {
      const storedAnnouncementIds = localStorage.getItem(SEEN_ANNOUNCEMENT_STORAGE_KEY);
      state.announcements.seenAnnouncementIds =
        storedAnnouncementIds != null ? (JSON.parse(storedAnnouncementIds) as string[]) : [];
    },
    setSeenAnnouncementId(state, action: PayloadAction<string>) {
      const seenCurrentIds =
        state.announcements.data
          ?.filter(
            (a) =>
              a.id !== action.payload && state.announcements.seenAnnouncementIds?.includes(a.id),
          )
          .map((a) => a.id) ?? [];
      const newSeenIds = [...seenCurrentIds, action.payload];
      state.announcements.seenAnnouncementIds = newSeenIds;
      localStorage.setItem(SEEN_ANNOUNCEMENT_STORAGE_KEY, JSON.stringify(newSeenIds));
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getStatsMissionEntries.pending, (state, action) => {
      const buildSet = state.missionStats.entries.find(
        (bS) =>
          bS.buildNumber === action.meta.arg.buildNumber &&
          bS.softwareVersion === action.meta.arg.softwareVersion,
      );
      if (buildSet != null) {
        buildSet.rest.getStatus = HttpFetchStatus.Loading;
      }
    });
    builder.addCase(getStatsMissionEntries.fulfilled, (state, action) => {
      const buildSet = state.missionStats.entries.find(
        (bS) =>
          bS.buildNumber === action.meta.arg.buildNumber &&
          bS.softwareVersion === action.meta.arg.softwareVersion,
      );
      if (buildSet != null) {
        buildSet.rest.getStatus = HttpFetchStatus.Success;
        buildSet.count = action.payload.count;
        buildSet.data = action.payload.data;
        buildSet.start = action.payload.start;
        buildSet.end = action.payload.end;
      }
    });
    builder.addCase(getStatsMissionEntries.rejected, (state, action) => {
      const buildSet = state.missionStats.entries.find(
        (bS) =>
          bS.buildNumber === action.meta.arg.buildNumber &&
          bS.softwareVersion === action.meta.arg.softwareVersion,
      );
      if (buildSet != null) {
        buildSet.rest.getStatus = HttpFetchStatus.Failed;
        buildSet.rest.getError = action.payload ?? null;
      }
    });

    builder.addCase(getAnnouncements.pending, (state, action) => {
      state.announcements.rest.getStatus = HttpFetchStatus.Loading;
    });
    builder.addCase(getAnnouncements.fulfilled, (state, action) => {
      state.announcements.rest.getStatus = HttpFetchStatus.Success;
      state.announcements.data = action.payload;
    });
    builder.addCase(getAnnouncements.rejected, (state, action) => {
      state.announcements.rest.getStatus = HttpFetchStatus.Failed;
      state.announcements.rest.getError = action.payload ?? null;
    });
  },
});

export const {
  resetServiceState,
  toggleBuildNumberAndVersion,
  updateMissionFilters,
  fetchSeenAnnouncementIds,
  setSeenAnnouncementId,
  resetAnnouncementsRestStatus,
} = serviceSlice.actions;

export default serviceSlice.reducer;
